import { Box, MenuItem, Select, type SelectChangeEvent, styled } from "@mui/material";
import React, { useMemo } from "react";

import { ariaLabel } from "src/constants/app/aria-labels";
import { getFutureYearOptions, getPastYearOptions } from "src/utils";

export type DateType = {
  year: number;
  month: number | null;
} | null;

interface Props {
  dateFilter: DateType;
  period: "PAST" | "FUTURE";
  onChangeDate: (e: SelectChangeEvent<unknown>) => void;
  onChangeMonth: (e: SelectChangeEvent<unknown>) => void;
}

const StyledSelectField = styled(Select)`
  background-color: ${(props) => props.theme.palette.common.white};
`;

export const NO_SELECTED_CATEGORIES_VALUE = "_all";

const MonthsLabel = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const months = [...Array(12)].map((_, index) => {
  return { label: MonthsLabel[index], value: index + 1 };
});

export const DateFilter = ({ dateFilter, period, onChangeDate, onChangeMonth }: Props) => {
  const years = useMemo(() => {
    return period === "PAST" ? getPastYearOptions() : getFutureYearOptions();
  }, [period]);

  const isMonthFieldDisabled = useMemo(() => {
    return typeof dateFilter?.year !== "number";
  }, [dateFilter]);

  return (
    <Box component="td" sx={{ display: "flex", gap: 2 }}>
      <StyledSelectField
        fullWidth
        variant="outlined"
        value={dateFilter?.year || NO_SELECTED_CATEGORIES_VALUE}
        onChange={onChangeDate}
        inputProps={{ "aria-label": ariaLabel.yearSelect }}
        sx={{
          "& .Mui-disabled": {
            "-webkit-text-fill-color": (theme) => `${theme.palette.black.main} !important`,
          },
        }}
      >
        <MenuItem value={NO_SELECTED_CATEGORIES_VALUE}>
          <em>Année</em>
        </MenuItem>
        {years.map((year, index) => (
          <MenuItem key={index} value={year}>
            {year}
          </MenuItem>
        ))}
      </StyledSelectField>

      <StyledSelectField
        disabled={isMonthFieldDisabled}
        fullWidth
        variant="outlined"
        value={dateFilter?.month || NO_SELECTED_CATEGORIES_VALUE}
        onChange={onChangeMonth}
        inputProps={{ "aria-label": ariaLabel.monthSelect }}
        sx={{
          "&.Mui-disabled": {
            "-webkit-text-fill-color": (theme) => `${theme.palette.black.main} !important`,
            opacity: 0.5,
          },
          "&.Mui-disabled .MuiSelect-select": {
            cursor: "not-allowed !important",
          },
        }}
      >
        <MenuItem value={NO_SELECTED_CATEGORIES_VALUE}>
          <em>Mois</em>
        </MenuItem>
        {months.map((month, index) => (
          <MenuItem key={index} value={month.value}>
            {month.label}
          </MenuItem>
        ))}
      </StyledSelectField>
    </Box>
  );
};
